import React from 'react';
import { Grid, Stack } from '@mui/material';
import { CloseRounded } from '@mui/icons-material';
import { Button, ButtonVariantProps } from '../../atoms/Button';
import { Typography, TypographyFontWeightProps, TypographyVariantProps } from '../../atoms/Typography';
import { FormBuilderCondition } from '../../molecules/FormBuilderCondition';
import { FormBuilderField } from '../../organisms/FormBuilderField';
import { FormBuilderContainerProps } from './FormBuilderContainer.props';
import { ContainerGrid, CloseButtonWrapper, StyledTextField } from './FormBuilderContainer.styles';
import { isEmptyString } from '../../../utils/common';
import { FormSubConditionItem } from '../../../@types';

export const FormBuilderContainer: React.FC<FormBuilderContainerProps> = ({
  subFieldTypes,
  isSelected,
  showErrors,
  value,
  onChange,
  onSelectContainer,
  onRemove,
  selectedSubConditional,
  setSelectedSubConditional,
  subConditionalConditions
}) => {
  return (
    <ContainerGrid 
      container 
      spacing={2} 
      borderColor={isSelected ? 'red' : 'black'} 
      onClick={(event) => {
        event.stopPropagation();
        onSelectContainer(value.id);
        !value.isSubContainer && setSelectedSubConditional({ id: 0, selectedOptionId: 0 });
      }}
    >
      <Grid size={{ xs: 12 }} pl={2}>
        <Stack direction="row" spacing={1} justifyContent="space-between">
          {(value?.conditions && value?.conditions.length > 0) &&
            <Typography variant={TypographyVariantProps.Body2} fontWeight={TypographyFontWeightProps.Bold}>
              -- Conditions --
            </Typography>
          }
          {!value.isSubContainer &&
            <CloseButtonWrapper>
              <Button
                variant={ButtonVariantProps.Icon}
                onClick={() => onRemove(value.id)}
              >
                <CloseRounded fontSize="small" color="error" />
              </Button>
            </CloseButtonWrapper>
          }
        </Stack>
      </Grid>

      {value?.conditions.map((condition) => (
        <Grid key={condition.id} size={{ xs: 12 }}>
          <FormBuilderCondition 
            value={condition}
            onChange={(val) => {
              const modifiedConditions = value.conditions.map((obj) => {
                return (obj.id === val.id) ? val : obj;
              });

              onChange({ ...value, conditions: modifiedConditions });
            }}
            onRemove={(id) => {
              const modifiedConditions = value.conditions.filter((obj) => obj.id !== id);
              onChange({ ...value, conditions: modifiedConditions });
            }}
          />
        </Grid>
      ))}

      <Grid size={{ xs: 12 }} pl={2}>
        <Stack direction="row" spacing={1} justifyContent="space-between">
          {(value?.subConditions && value?.subConditions.length > 0) &&
            <Typography variant={TypographyVariantProps.Body2} fontWeight={TypographyFontWeightProps.Bold}>
              -- Sub Conditional --
            </Typography>
          }
        </Stack>
      </Grid>

      {value?.subConditions?.map((subCondition) => (
        <Grid key={subCondition.id} size={{ xs: 12 }}>
          <FormBuilderCondition
            isSubConditional
            value={subCondition}
            onChange={(val) => {
              const modifiedSubConditions = value.subConditions?.map((obj) => {
                return (obj.id === val.id) ? { 
                  ...val ,
                  thenContainer: obj.thenContainer,
                  elseContainer: obj.elseContainer
                } : obj;
              });
              onChange({ ...value, subConditions: modifiedSubConditions });
            }}
            onRemove={(id) => {
              const modifiedSubConditions = value.subConditions?.filter((obj) => obj.id !== id);
              onChange({ ...value, subConditions: modifiedSubConditions });
            }}
            subConditionalConditions={subConditionalConditions}
          />
          <Grid container key={subCondition.id} size={{ xs: 12, md: 12 }}>
            <Grid key={subCondition?.thenContainer?.id} size={{ xs: 12, md: 6 }} padding={2}>
              <Grid size={{ xs: 12 }} pl={2}>
                <Typography variant={TypographyVariantProps.Body2} fontWeight={TypographyFontWeightProps.Bold}>
                  -- Then --
                </Typography>
              </Grid>
              <FormBuilderContainer
                subFieldTypes={subFieldTypes}
                isSelected={selectedSubConditional.id === subCondition.id && selectedSubConditional.selectedOptionId === subCondition?.thenContainer?.id} 
                showErrors={showErrors} 
                value={subCondition?.thenContainer} 
                onChange={(val) => {
                  subCondition.thenContainer = val;
                  const modifiedSubConditions = value.subConditions?.map((condition) => {
                    return (condition.id === subCondition.id) ? subCondition : condition;
                  });
                  onChange({ ...value, subConditions: modifiedSubConditions as FormSubConditionItem[] });
                }} 
                onSelectContainer={() => {
                  onSelectContainer(value.id);
                  setSelectedSubConditional({ id: subCondition.id, selectedOptionId: 1 });
                }}    
                onRemove={() => null}
                selectedSubConditional={selectedSubConditional}
                setSelectedSubConditional={setSelectedSubConditional}
              />
            </Grid>
            <Grid key={subCondition?.elseContainer?.id} size={{ xs: 12, md: 6 }} padding={2}>
              <Grid size={{ xs: 12 }} pl={2}>
                <Typography variant={TypographyVariantProps.Body2} fontWeight={TypographyFontWeightProps.Bold}>
                   -- Else --
                </Typography>
              </Grid>
              <FormBuilderContainer
                subFieldTypes={subFieldTypes}
                isSelected={selectedSubConditional.id === subCondition.id && selectedSubConditional.selectedOptionId === subCondition?.elseContainer?.id} 
                showErrors={showErrors} 
                value={subCondition?.elseContainer} 
                onChange={(val) => {
                  subCondition.elseContainer = val;
                  const modifiedSubConditions = value.subConditions?.map((condition) => {
                    return (condition.id === subCondition.id) ? subCondition : condition;
                  });
                  onChange({ ...value, subConditions: modifiedSubConditions as FormSubConditionItem[] });
                }} 
                onSelectContainer={() => {
                  onSelectContainer(value.id);
                  setSelectedSubConditional({ id: subCondition.id, selectedOptionId: 2 });
                }}    
                onRemove={() => null}
                selectedSubConditional={selectedSubConditional}
                setSelectedSubConditional={setSelectedSubConditional}   
              />
            </Grid>
          </Grid>
        </Grid>
      ))}

      {value.isDecisionPrompt && (
        <>
          <Grid size={{ xs: 12 }} pl={2}>
            <Typography variant={TypographyVariantProps.Body2} fontWeight={TypographyFontWeightProps.Bold}>
            -- Decision Prompt --
            </Typography>
          </Grid>
          <Grid marginRight="15px" size={{ xs: 12 }} pl={2}>
            <StyledTextField
              fullWidth
              label="Question text"
              required={true}
              error={showErrors && isEmptyString(value.questionData?.questionText)}
              value={value.questionData?.questionText}
              onChange={(val) => onChange({
                ...value,
                questionData: {
                  id: value.id,
                  questionText: val,
                  isYes: false
                }
              })} />
          </Grid>
        </>
      )}

      <Grid size={{ xs: 12 }} pl={2}>
        <Typography variant={TypographyVariantProps.Body2} fontWeight={TypographyFontWeightProps.Bold}>
          -- Fields --
        </Typography>
      </Grid>
      {value.fields.map((field) => (
        <Grid key={field.id} size={{ xs: 12 }}>
          <FormBuilderField
            subFieldTypes={subFieldTypes}
            showErrors={showErrors}
            value={field}
            onChange={(val) => {
              const modifiedFields = value.fields.map((obj) => {
                return (obj.id === val.id) ? val : obj;
              });

              onChange({ ...value, fields: modifiedFields });
            }}
            onRemove={(id) => {
              const modifiedFields = value.fields.filter((obj) => obj.id !== id);
              onChange({ ...value, fields: modifiedFields });
            }}
            isSubContainer={value?.isSubContainer}
          />
        </Grid>
      ))}
    </ContainerGrid>
  );
};
